import { useEffect, useRef, useState } from "react";
import { createMap, tealPin, L } from "../lib/leafletMap";
import api from "../lib/api";

// Wave 25.17 — Free trip-route map. Renders a Leaflet map with stops as
// numbered pins and (optionally) the OSRM-driven driving polyline.
//
// Props:
//   points  — [{ label, lat, lng, place_id, kind, title, idx }]
//   height  — CSS height (string or number)
//   className — extra wrapper classes
export default function TripRouteMap({ points = [], height = "100%", className = "" }) {
  const mapDiv = useRef(null);
  const mapRef = useRef(null);
  const [routeErr, setRouteErr] = useState("");

  // Only points with coords can be plotted/routed.
  const usable = points.filter((p) => p.lat != null && p.lng != null);

  useEffect(() => {
    if (!mapDiv.current) return undefined;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const initialCenter = usable[0] ? [usable[0].lat, usable[0].lng] : [-25.4753, 30.9694];
    const { map } = createMap(mapDiv.current, { center: initialCenter, zoom: 6 });
    mapRef.current = map;

    const markers = [];
    usable.forEach((p, i) => {
      const label = p.kind === "depart" ? "▶" : (p.idx ?? i);
      const m = L.marker([p.lat, p.lng], { icon: tealPin(String(label)) }).addTo(map);
      m.bindTooltip(`${p.title || `Stop ${i + 1}`}${p.label ? ` — ${p.label}` : ""}`, {
        direction: "top", offset: [0, -28],
      });
      markers.push(m);
    });
    if (markers.length > 0) {
      const group = L.featureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.2));
      if (markers.length === 1) map.setZoom(13);
    }

    // Fetch + draw the OSRM route line (best-effort).
    if (usable.length >= 2) {
      const coords = usable.map((p) => `${p.lng},${p.lat}`).join(";");
      // Use our own backend if you ever want to proxy + cache; the
      // public OSRM is fine for now and free.
      fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`)
        .then((r) => r.json())
        .then((j) => {
          if (j?.code !== "Ok" || !j.routes?.[0]) return;
          const geo = j.routes[0].geometry;
          const layer = L.geoJSON(geo, {
            style: { color: "#1FB6C1", weight: 4, opacity: 0.85 },
          }).addTo(map);
          map.fitBounds(layer.getBounds().pad(0.15));
        })
        .catch(() => setRouteErr("Could not fetch driving route line."));
    }

    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_e) { /* ignore */ }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(usable.map((p) => [p.lat, p.lng]))]);

  if (usable.length === 0) {
    return (
      <div className={`hv-card p-8 text-center text-xs text-[#647087] ${className}`} style={{ height }}>
        No stops with coordinates yet — add at least 2 with pins or address autocomplete to render the route.
      </div>
    );
  }

  return (
    <div className={`hv-card overflow-hidden p-0 relative ${className}`} style={{ height }}>
      <div ref={mapDiv} className="w-full h-full" data-testid="trip-route-map" />
      {routeErr && (
        <div className="absolute bottom-2 left-2 text-[10px] text-amber-300 bg-black/60 px-2 py-1 rounded">
          {routeErr}
        </div>
      )}
    </div>
  );
}

// Helper to ensure trip points include lat/lng — falls back to address
// resolve via /places/details when only place_id is present.
export async function ensureTripPointCoords(points) {
  const out = [];
  for (const p of points) {
    if (p.lat != null && p.lng != null) {
      out.push(p);
      continue;
    }
    if (p.place_id) {
      try {
        const { data } = await api.get("/places/details", { params: { place_id: p.place_id } });
        if (data?.lat != null && data?.lng != null) {
          out.push({ ...p, lat: data.lat, lng: data.lng });
          continue;
        }
      } catch (_e) { /* ignore */ }
    }
    out.push(p);
  }
  return out;
}
